import { pricingGoogsQueryOption } from "@/apis/query/pricing"
import { useUserDetailStore } from "@/store/useUserDetailStore"
import { LightButton } from "@/ui/Button"
import { AdFree } from "@/ui/icons/AdFree"
import { Check } from "@/ui/icons/Check"
import { Luxury } from "@/ui/icons/Luxury"
import { PricingTabHeader } from "@/ui/icons/PricingTabHeader"
import { Synergy } from "@/ui/icons/Synergy"
import { MembershipBadge } from "@/components/MembershipBadge"
import { opacityAnimation } from "@/util/animation"
import { Button, cn, Divider, Image, ScrollShadow } from "@heroui/react"
import { useQuery } from "@tanstack/react-query"
import { AnimatePresence, motion } from "framer-motion"
import { useLocale, useTranslations } from "next-intl"
import NextImage from "next/image"
import Link from "next/link"
import { useMemo, useState } from "react"
import { useShowPricing } from "."
import { Pending } from "../Pending"
import PricingFormWrapper from "./PricingFormWrapper"
import VipExpired from "./VipExpired"

/** vip_type: 1 Premium(Luxury) / 2 Royal Membership(原 Synergy) / 3 AD-Free(新增) */
const VIP_META = [
  {
    vipType: 1,
    titleKey: "pricing.premium" as const,
    asideTitleKey: "pricing.luxuryPremium" as const,
    content: "Unlimited Energy For Messages & No Ads",
    iconSrc: "/icons/Luxury.svg",
    Icon: Luxury,
  },
  {
    vipType: 2,
    titleKey: "pricing.royalMembership" as const,
    asideTitleKey: "pricing.royalMembership" as const,
    content: "Unlimited Messaging with Characters & Voice Usage",
    iconSrc: "/icons/Synergy.svg",
    Icon: Synergy,
  },
  {
    vipType: 3,
    titleKey: "pricing.adFree" as const,
    asideTitleKey: "pricing.adFree" as const,
    content: "Remove all ads across the experience",
    iconSrc: "/icons/AdFree.svg",
    Icon: AdFree,
  },
]

const VipTypeIcon = ({ vipType, width = 28, height = 28 }: { vipType: number; width?: number; height?: number }) => {
  const meta = VIP_META.find(item => item.vipType === vipType) || VIP_META[0]
  const Icon = meta.Icon
  return <Icon width={width} height={height} />
}

export default () => {

  const t = useTranslations()
  const locale = useLocale()
  const [avatar, name, vip, vipType, adFree] = useUserDetailStore(state => [state.avatar, state.name, state.vip, state.vip_type, state.ad_free])

  const showPricing = useShowPricing()
  const { data, isPending } = useQuery(pricingGoogsQueryOption(1))
  const [active, setActive] = useState(0)
  const goods = useMemo(() => {
    return data?.data.goods || []
  }, [data])

  const tabs = useMemo(() => {
    // 始终按设计稿展示 3 档；商品优先按 vip_type 匹配，否则按下标回退
    return VIP_META.map((meta, index) => {
      const matched = goods.find(item => item?.vip_type === meta.vipType) || goods[index]
      return { ...meta, goods: matched }
    })
  }, [goods])

  const safeActive = Math.min(active, Math.max(tabs.length - 1, 0))
  const activeTab = tabs[safeActive] || tabs[0]

  return <div className="max-w-[1130px] h-full w-full bg-contain bg-no-repeat lg:bg-[url('/imgs/pring-header-bg.png')]">
    {/* Header */}
    <header className="w-full relative px-8 py-7 flex h-[120px] justify-between items-center bg-cover bg-[url('/imgs/pring-header-bg.png')] lg:bg-none">
      <div className="flex gap-3 items-center">
        <Image radius="full" classNames={{ wrapper: "border-1 border-theme-white flex-shrink-0", img: "w-14 h-14 lg:w-16 lg:h-16" }} src={avatar} />
        <div>
          <div className="flex items-center gap-2">
            <p className="text-theme-white text-[20px]">{name}</p>
            {(vip && vipType) || adFree ? (
              <span className="flex-shrink-0 flex items-center justify-center h-7 w-9 rounded-lg bg-black/40">
                {vip && vipType
                  ? <VipTypeIcon vipType={vipType} width={28} height={28} />
                  : <MembershipBadge vip={vip} vipType={vipType} adFree={adFree} width={28} height={28} />}
              </span>
            ) : null}
          </div>
          <p className="text-theme-white/40 text-[14px] mt-[6px] flex gap-1">
            <VipExpired />
            {
              locale === 'ru' && <LightButton size="sm" className="text-white" as={Link} href="/ru/subscription">Управление подпиской</LightButton>
            }
          </p>

        </div>
      </div>
      <Button className="bg-transparent absolute right-4 bottom-4 lg:relative text-[12px] lg:text-[16px]  text-[#FFE49D]" size="sm" disableRipple onPress={() => showPricing('energy')}>Buy More Energy</Button>
    </header>

    <main className="hidden lg:block min-h-[620px] h-full relative pl-[280px]">
      {/* 左边 */}
      <aside className="absolute h-full w-[280px] bg-aside-color left-0 top-0 rounded-bl-[16px] overflow-hidden">
        {isPending || !activeTab?.goods ?
          <div className="pt-9"><Pending /></div> :
          <AnimatePresence mode="wait">
            <motion.div key={safeActive} {...opacityAnimation} className="bg-aside-color px-[22px] pt-9">
              <AsideContent
                goods={activeTab.goods}
                title={t(activeTab.asideTitleKey)}
                content={activeTab.content}
              />
            </motion.div>
          </AnimatePresence>
        }
      </aside>

      {/* pc */}
      <section className="rounded-br-[16px] h-full min-h-[620px] hidden lg:flex flex-col ">
        <div className="h-16 flex-shrink-0 overflow-visible hidden lg:flex items-end">
          {tabs.map((tab, index) => {
            const Icon = tab.Icon
            const isActive = safeActive === index
            const isFirst = index === 0
            const isLast = index === tabs.length - 1
            return (
              <Button
                key={tab.vipType}
                onPress={() => setActive(index)}
                data-active={isActive}
                disableAnimation
                radius="none"
                className={cn(`group relative flex-1 overflow-visible h-full bg-transparent !opacity-100 p-0 flex justify-center gap-2 data-[active=true]:z-10 ${!isFirst ? "ml-[-18px]" : ""}`,
                  'data-[active=true]:h-[68px] data-[active=true]:rounded-t-md',
                  "bg-[#F0F1F3] data-[active=true]:bg-white dark:bg-[#16181D]"
                )}
              >
                {/* <PricingTabHeader
                  className={`text-[#DAD8EF] w-full top-0 absolute h-full !max-w-none group-data-[active=true]:text-white dark:group-data-[active=true]:text-[#1E2128] transition-colors ${isFirst ? "right-0" : isLast ? "left-0 [transform:scaleX(-1)]" : "left-1/2 -translate-x-1/2"}`}
                /> */}
                <div className="relative z-10 flex gap-2 items-center text-theme-primary/70 group-data-[active=true]:text-theme-primary justify-center h-full px-2">
                  <Icon width={32} height={32} />
                  <span className="text-[16px] xl:text-[18px] font-bold text-balance">{t(tab.titleKey)}</span>
                </div>
              </Button>
            )
          })}
        </div>

        <div className="px-6 bg-surface flex-1">
          {isPending || !activeTab?.goods ?
            <div className="pt-9"><Pending /></div> :
            <PricingFormWrapper className="h-full pt-4" goods={activeTab.goods} />
          }
        </div>
      </section>


    </main>

    {/* 移动端 */}
    <main className="h-[calc(100%_-_120px)] flex lg:hidden flex-col items-center">

      {/* 移动端 tab*/}
      <div className="flex flex-shrink-0 items-center mt-6 w-full rounded-full px-[2px] bg-[#DAD8EF] text-center font-bold max-w-[360px] text-[12px] sm:text-[14px] h-11">
        {tabs.map((tab, index) => (
          <Button key={tab.vipType} onPress={() => setActive(index)} className="flex-1 px-1 !bg-transparent font-bold min-w-0" disableRipple>
            <span className="truncate">{t(tab.titleKey)}</span>
            {
              safeActive === index &&
              <motion.div
                layoutId="my-tab-indicator"
                className="absolute inset-0 bg-surface rounded-full z-[-1]"
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            }
          </Button>
        ))}
      </div>

      <ScrollShadow className="flex-1 overflow-auto my-3 w-full">

        <div className="pl-4">
          {/* 会员详情 */}
          {isPending || !activeTab?.goods ?
            <div className="pt-6"><Pending /></div> :
            <AnimatePresence mode="wait">
              <motion.div className="pr-4" key={safeActive} {...opacityAnimation}>
                <MobileContent goods={activeTab.goods} title={t(activeTab.asideTitleKey)} />
              </motion.div>
            </AnimatePresence>
          }

          {/* 价格详情 */}
          <div className="bg-surface">
            {isPending || !activeTab?.goods ?
              <div className="pt-9"><Pending /></div> :
              <PricingFormWrapper goods={activeTab.goods} />
            }
          </div>
        </div>

      </ScrollShadow>

    </main>

  </div>
}


const AsideContent = ({ goods, title, content }) => {
  const t = useTranslations()
  const meta = VIP_META.find(item => item.vipType === goods.vip_type) || VIP_META[0]
  const Icon = meta.Icon
  return (
    <div className="flex flex-col items-center">
      <Icon width={60} height={60} />
      <h3 className="text-[20px] font-bold mt-3 text-balance">{title}</h3>
      <h5 className="mt-3 text-[12px] text-theme-primary/70 text-center text-pretty">{content}</h5>
      <Divider className="bg-theme-primary/5 my-6" />
      <h4 className="w-full text-start font-bold">{t('pricing.privileges')}</h4>
      <ul className="w-full text-start flex flex-col mt-4 gap-[10px]">
        {
          (goods.explain || []).map(item => (
            <li key={item} className="flex gap-3 text-[12px] text-[#9CADD0]"><Check className="text-purple-blue" width={12} /><span className="text-pretty">{item}</span></li>
          ))
        }
      </ul>
    </div>
  )
}

const MobileContent = ({ goods, title }) => {
  return <div className="w-full mt-3">

    <div className="pl-4 mx-auto w-fit">
      <h3 className="text-[24px] font-bold text-start text-balance">{title}</h3>
      <ul className="w-full text-start flex flex-col mt-4 gap-[10px] pl-2">
        {
          (goods.explain || []).map(item => (
            <li key={item} className="flex gap-3 text-[12px] text-[#9CADD0]"><Check className="text-purple-blue" width={12} /><span className="text-pretty">{item}</span></li>
          ))
        }
      </ul>
    </div>

    <div className="mt-5 relative h-6 flex items-center w-[80%]">
      <Divider className="bg-theme-primary/5 w-full" />
      <div className="absolute right-0 flex gap-8">
        {VIP_META.map(item => (
          <NextImage
            key={item.vipType}
            style={{ opacity: goods.vip_type === item.vipType ? 1 : 0.5 }}
            src={item.iconSrc}
            width={24}
            height={24}
            alt={item.titleKey}
            loading="eager"
          />
        ))}
      </div>

    </div>
  </div>
}
